import React from "react";

/**
 * Component decorator for allowing dependency injection of sub-components.
 *
 * Example usage:
 *
 * ```tsx
 * const ListItem = (props: React.PropsWithChildren) => {
 *     return <li>{props.children}</li>;
 * };
 *
 * const orderedListDeps = {
 *     ListItem,
 * };
 * type OrderedListProps = {
 *     items: string[];
 * };
 * const OrderedList = injectComponents(orderedListDeps)<OrderedListProps>(
 *     (props) => {
 *         const { ListItem } = props.dependencies;
 *         return (
 *             <ol>
 *                 {props.items.map((item) => (
 *                     <ListItem key={item}>{item}</ListItem>
 *                 ))}
 *             </ol>
 *         );
 *     },
 * );
 * ```
 *
 * The example `OrderedList` component above uses `injectComponents` to allow
 * a user of the component to inject a replacement for the default implementation
 * of the `ListItem` sub-component.
 *
 * By default, nothing has to be injected:
 *
 * ```tsx
 * <OrderedList
 *     items={["a", "b", "c"]}
 * />
 * ```
 *
 * But optionally, a custom version of ListItem could be injected, as long as it
 * adheres to the same interface as the original ListItem implementation.
 *
 * ```tsx
 * const CustomListItem = (props: React.PropsWithChildren) => {
 *     return <li className="custom-list-item">{props.children}</li>;
 * };
 * ```
 *
 * ```tsx
 * <OrderedList
 *     items={["a", "b", "c"]}
 *     dependencies={{
 *         ListItem: CustomListItem,
 *     }}
 * />
 * ```
 */
export const injectComponents = <
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    DefaultDependencies extends { [name: string]: React.ComponentType<any> },
>(
    defaultDependencies: DefaultDependencies,
) => {
    // Rebuild expected dependencies type (instead of just using
    // DefaultDependencies directly) to eliminate funkiness when a default
    // dependency is wrapped with forwardRef.
    type Dependencies = {
        [Name in keyof DefaultDependencies]: React.ComponentType<
            React.ComponentProps<DefaultDependencies[Name]>
        >;
    };
    type DependencyProps = {
        dependencies: Dependencies;
    };
    type PartialDependencyProps = {
        dependencies?: Partial<Dependencies>;
    };
    const injectorFactory = <PassThruProps extends object>(
        InnerComponent: React.ComponentType<PassThruProps & DependencyProps>,
    ) => {
        type InnerProps = PassThruProps & DependencyProps;
        type OuterProps = PassThruProps & PartialDependencyProps;
        type InjectorComponent = React.ComponentType<OuterProps> &
            Readonly<{
                dependencies: Dependencies;
            }>;
        const InjectorFn: React.ComponentType<OuterProps> = (outerProps) => {
            const innerProps: InnerProps = {
                ...outerProps,
                dependencies: {
                    ...defaultDependencies,
                    ...outerProps.dependencies,
                },
            };
            return <InnerComponent {...innerProps} />;
        };
        const Injector: InjectorComponent = Object.assign(InjectorFn, {
            dependencies: defaultDependencies,
        });
        return Injector;
    };
    return injectorFactory;
};
